import React, { useEffect, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import { HelperMessage } from '../../common/ui/helper-message/index.tsx';
import {
	usePromptId,
	usePromptIsLoading,
	usePromptResponse,
	useScopedId,
} from '../../controllers/prompt-state/index.tsx';
import type { PromptId } from '../../controllers/prompt-state/types.tsx';

export type Props<Payload> = {
	children: (response: Payload, promptId: PromptId) => ReactNode;

	/**
	 * Callback prop for emiting events related to NL to JQL search result
	 * @argument args.promptId - Single Instrumentation ID of the NL prompt used for the search
	 * @argument args.nlResponseType - whether the resulting search is a success, warning or an error
	 * @argument args.is5xxError - whether the search request caused a 5xx error
	 */
	onNLEvent: (args: {
		promptId: string;
		nlResponseType: 'success' | 'warning' | 'error';
		// Make required field when doing FF cleanup for nl_to_jql_update_aierror_not_fired_on_4xx
		is5xxError?: boolean;
	}) => void;
};

/**
 * @deprecated
 * This component is currently used by NL-to-JQL experience only.
 * Historically, this was intended to be a generic AI component. However, that assumption doesn't hold anymore.
 * Other AI experiences should not use this component due to the unnecessary complexity added by this architecture.
 *
 * Even the NL-to-JQL experience is planned to be rearchitectured to get rid of this complexity.
 * This idea of a rearchitecture to reduce complextiy has been run across the whole team, and has received unanimous support.
 * However, there is no planned date for that since Gravity team is focusing on other priorities as of today.
 *
 * Since this is a deprecated component used only by NL-to-JQL, we are going to leak some NL-to-JQL specific implementation details to this component.
 * These details can be removed with the NL-to-JQL rearchitecture.
 */
export function PromptResponse<Payload>({ children, onNLEvent }: Props<Payload>) {
	const [response] = usePromptResponse<Payload>();
	const [isLoading] = usePromptIsLoading();
	const [searchFieldId] = useScopedId('ai-search-field');
	const [promptId] = usePromptId();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		if (fg('nl_to_jql_update_aierror_not_fired_on_4xx')) {
			if (response?.type && response?.is5xxError !== undefined && promptId) {
				onNLEvent({
					promptId,
					nlResponseType: response.type,
					is5xxError: response.is5xxError,
				});
			}
		} else if (response?.type && promptId) {
			onNLEvent({
				promptId,
				nlResponseType: response.type,
			});
		}
	}, [createAnalyticsEvent, promptId, response?.type, response?.is5xxError, onNLEvent]);

	if (response === undefined) {
		return null;
	}

	switch (response.type) {
		case 'success':
			return <ResponseContainer>{children(response.payload, promptId)}</ResponseContainer>;
		case 'warning':
			// Hide warning message while request is in-flight
			return isLoading ? null : (
				<ResponseContainer>
					<HelperMessage
						appearance="warning"
						describedBy={searchFieldId}
						testId="atlassian-intelligence.ui.prompt-response.warning"
					>
						{response.payload}
					</HelperMessage>
				</ResponseContainer>
			);
		case 'error':
			// Hide error message while request is in-flight
			return isLoading ? null : (
				<ResponseContainer>
					<HelperMessage
						appearance="error"
						describedBy={searchFieldId}
						testId="atlassian-intelligence.ui.prompt-response.error"
					>
						{response.payload}
					</HelperMessage>
				</ResponseContainer>
			);
		default:
			return null;
	}
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ResponseContainer = styled.div({
	marginTop: token('space.100'),
	marginRight: token('space.150'),
	marginBottom: token('space.100'),
	marginLeft: token('space.150'),
});
